/* eslint-disable @typescript-eslint/explicit-function-return-type */

import { css, Interpolation, ThemeProps } from 'styled-components';
import { Breakpoint, NonMobileBreakpoint, Theme } from '../theme';

export type MediaQuery = <P extends ThemeProps<Theme>>(
  strings: TemplateStringsArray,
  ...interpolations: Interpolation<P>[]
) => Interpolation<P>;

const generateMediaQuery: (breakpoint: NonMobileBreakpoint) => MediaQuery = (
  breakpoint
) => (strings, ...interpolations) => css`
  @media (min-width: ${({ theme }) => theme.layout[breakpoint].breakpointMinPx}px) {
    ${css(strings, ...interpolations)}
  }
`;

/**
 * Interpolation functions for each breakpoint to wrap some css in a media query.
 *
 * Note: mobile styling is the default and so does not need a mediaQuery associated
 * with it.
 *
 * @example
 * ${mediaQuery.desktop`
 *   margin: 0 0 ${spacing(6)} 0;
 * `}
 *
 */
const mediaQuery = {
  tabletPortrait: generateMediaQuery('tabletPortrait'),
  tabletLandscape: generateMediaQuery('tabletLandscape'),
  desktop: generateMediaQuery('desktop'),
};

type StyleGenerator<P extends ThemeProps<Theme>> = (
  breakpoint: Breakpoint
) => Interpolation<P>;

export const stylePerBreakpoint = <P extends ThemeProps<Theme> = ThemeProps<Theme>>(
  style: StyleGenerator<P>
): Interpolation<P> => {
  const mediaQueries = Object.entries(mediaQuery).map(
    ([breakpoint, query]) => query`${style(breakpoint as Breakpoint)}`
  );
  /*  We add the mobile style to the **start** of the media queries, so that it doesn't
      override the media queries for the larger breakpoints.
  */
  mediaQueries.unshift(style('mobile'));
  return mediaQueries;
};

export default mediaQuery;
